import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/apiClient';
import type { 
  ReleaseFlowDto, 
  CreateReleaseFlowDto, 
  UpdateReleaseFlowDto,
  ReleaseFlowValidationResultDto,
  PaginatedResponse,
  ReleaseFlowNodePipelineLinkDto,
  CreateReleaseFlowNodePipelineLinkDto,
  UpdateReleaseFlowNodePipelineLinkDto
} from '../types/releaseFlow';

export interface UseReleaseFlowsOptions {
  isActive?: boolean;
  nameFilter?: string;
  page?: number;
  pageSize?: number;
}

export const useReleaseFlows = (options?: UseReleaseFlowsOptions) => {
  return useQuery<PaginatedResponse<ReleaseFlowDto>>({
    queryKey: ['releaseFlows', options],
    queryFn: () => apiClient.getReleaseFlows(options),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useReleaseFlow = (id: string | undefined) => {
  return useQuery<ReleaseFlowDto>({
    queryKey: ['releaseFlow', id],
    queryFn: () => apiClient.getReleaseFlow(id!),
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useCreateReleaseFlow = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ReleaseFlowDto, Error, CreateReleaseFlowDto>({
    mutationFn: apiClient.createReleaseFlow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseFlows'] });
    },
  });
};

export const useUpdateReleaseFlow = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ReleaseFlowDto, Error, { id: string; data: UpdateReleaseFlowDto }>({
    mutationFn: ({ id, data }) => apiClient.updateReleaseFlow(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['releaseFlows'] });
      queryClient.invalidateQueries({ queryKey: ['releaseFlow', data.id] });
    },
  });
};

export const useDeleteReleaseFlow = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: apiClient.deleteReleaseFlow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseFlows'] });
    },
  });
};

export const useValidateReleaseFlow = () => {
  return useMutation<ReleaseFlowValidationResultDto, Error, string>({
    mutationFn: apiClient.validateReleaseFlow,
  });
};

export const useValidateReleaseFlowDto = () => {
  return useMutation<ReleaseFlowValidationResultDto, Error, CreateReleaseFlowDto>({
    mutationFn: apiClient.validateReleaseFlowDto,
  });
};

// Hook for managing release flow editor state
export const useReleaseFlowEditor = () => {
  const createMutation = useCreateReleaseFlow();
  const updateMutation = useUpdateReleaseFlow();
  const validateDtoMutation = useValidateReleaseFlowDto();
  const validateMutation = useValidateReleaseFlow();

  const saveReleaseFlow = async (
    data: CreateReleaseFlowDto | (UpdateReleaseFlowDto & { id: string })
  ) => {
    if ('id' in data) {
      const { id, ...updateData } = data;
      return updateMutation.mutateAsync({ id, data: updateData });
    } else {
      return createMutation.mutateAsync(data);
    }
  };

  const validateReleaseFlow = async (data: CreateReleaseFlowDto) => {
    return validateDtoMutation.mutateAsync(data);
  };

  const validateExistingReleaseFlow = async (id: string) => {
    return validateMutation.mutateAsync(id);
  };

  return {
    saveReleaseFlow,
    validateReleaseFlow,
    validateExistingReleaseFlow,
    isSaving: createMutation.isPending || updateMutation.isPending,
    isValidating: validateDtoMutation.isPending || validateMutation.isPending,
    error: createMutation.error || updateMutation.error || validateDtoMutation.error || validateMutation.error,
  };
};

export const useAssociateNodePipelineLink = () => {
  const queryClient = useQueryClient();
  
  return useMutation<
    ReleaseFlowNodePipelineLinkDto, 
    Error, 
    { releaseFlowId: string; nodeId: string; data: CreateReleaseFlowNodePipelineLinkDto }
  >({
    mutationFn: ({ releaseFlowId, nodeId, data }) => 
      apiClient.associateNodePipelineLink(releaseFlowId, nodeId, data),
    onSuccess: (_, { releaseFlowId, nodeId }) => {
      queryClient.invalidateQueries({ 
        queryKey: ['releaseFlowNodePipelineLinks', releaseFlowId, nodeId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['releaseFlow', releaseFlowId] 
      });
    },
  });
};

export const useUpdateNodePipelineLink = () => {
  const queryClient = useQueryClient();
  
  return useMutation<
    ReleaseFlowNodePipelineLinkDto, 
    Error, 
    { 
      releaseFlowId: string; 
      nodeId: string; 
      data: UpdateReleaseFlowNodePipelineLinkDto 
    }
  >({
    mutationFn: ({ releaseFlowId, nodeId, data }) => 
      apiClient.updateNodePipelineLink(releaseFlowId, nodeId, data),
    onSuccess: (_, { releaseFlowId, nodeId }) => {
      queryClient.invalidateQueries({ 
        queryKey: ['releaseFlowNodePipelineLinks', releaseFlowId, nodeId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['releaseFlowNodePipelineLink', releaseFlowId, nodeId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['releaseFlow', releaseFlowId] 
      });
    },
  });
};